import moment from 'moment';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { contentLanguageCodeToi18nLanguage } from 'src/config/i18n.config';
import i18n from 'src/config/i18n';
import { saveI18nLanguageToLocalStorage } from 'src/shared/helpers/getPreferredLanguage';

import { getUserInformation } from '../selectors/userSelectors';
import {
    getContentLanguagesLoaded,
    getPreferredI18nLanguageFromLocalStoreOrGuessFromBrowser,
} from 'src/modules/languages/selectors/contentLanguagesSelectors';
import { fetchInitialData } from 'src/modules/auth/fetch/initialData';
import { useTranslation } from 'react-i18next';

export const LanguagePreferenceListener: React.FC = () => {
    const { contentLanguage } = useSelector(getUserInformation);
    const preferredI18nLanguageFromLocalStoreOrBrowser = useSelector(
        getPreferredI18nLanguageFromLocalStoreOrGuessFromBrowser,
    );
    const isContentLanguagesLoaded = useSelector(getContentLanguagesLoaded);

    const preferredLanguage = contentLanguage?.code;
    const preferredI18nLanguageFromUserPreference =
        preferredLanguage && contentLanguageCodeToi18nLanguage[preferredLanguage];
    const preferredI18nLanguage =
        preferredI18nLanguageFromUserPreference ?? preferredI18nLanguageFromLocalStoreOrBrowser;

    const {
        i18n: { language: currentLanguage },
    } = useTranslation();

    useEffect(() => {
        setMomentLocale(currentLanguage);
    }, [currentLanguage]);

    useEffect(() => {
        // Do not use i18n or language in this hook, it will create
        // a loop due to the i18n.changeLanguage call.

        async function updateLanguage(i18nLanguage: string) {
            await i18n.changeLanguage(i18nLanguage);

            saveI18nLanguageToLocalStorage(i18nLanguage);

            await fetchInitialData(false);
        }

        // Don't update the language until content languages
        // is available or a user preference is set.
        if (!isContentLanguagesLoaded && !preferredI18nLanguageFromUserPreference) return;

        // Only change the i18n language and re-fetch initial data if the language
        // has changed.
        if (i18n.language === preferredI18nLanguage) return;

        if (preferredI18nLanguage) {
            updateLanguage(preferredI18nLanguage);
        }
    }, [isContentLanguagesLoaded, preferredI18nLanguage, preferredI18nLanguageFromUserPreference]);

    return null;
};

const setMomentLocale = (language: string) => {
    moment.locale(language === 'zh-Hans' ? 'zh-CN' : language);
};
